import { Assets, Texture, Graphics, Container, type Renderer } from 'pixi.js';
import { Howl } from 'howler';

export interface LoadProgress {
  loaded: number;
  total: number;
  progress: number;
}

export type ProgressCallback = (progress: LoadProgress) => void;

/**
 * Asset loader for images, audio, and runtime-generated textures.
 */
export class AssetLoader {
  private textures: Map<string, Texture> = new Map();
  private sounds: Map<string, Howl> = new Map();
  private loadQueue: Array<{ key: string; url: string; type: 'image' | 'audio' }> = [];
  private renderer: Renderer | null = null;

  /**
   * Set the renderer for texture generation
   */
  setRenderer(renderer: Renderer): void {
    this.renderer = renderer;
  }

  /**
   * Add an image to the load queue
   */
  addImage(key: string, url: string): this {
    this.loadQueue.push({ key, url, type: 'image' });
    return this;
  }

  /**
   * Add audio to the load queue
   */
  addAudio(key: string, url: string): this {
    this.loadQueue.push({ key, url, type: 'audio' });
    return this;
  }

  /**
   * Load all queued assets
   */
  async load(onProgress?: ProgressCallback): Promise<void> {
    const total = this.loadQueue.length;
    let loaded = 0;

    for (const item of this.loadQueue) {
      try {
        if (item.type === 'image') {
          const texture = await Assets.load<Texture>(item.url);
          this.textures.set(item.key, texture);
        } else if (item.type === 'audio') {
          await this.loadSound(item.key, item.url);
        }
      } catch (error) {
        console.warn(`Failed to load asset: ${item.key} from ${item.url}`, error);
      }

      loaded++;
      if (onProgress) {
        onProgress({
          loaded,
          total,
          progress: loaded / total,
        });
      }
    }

    this.loadQueue = [];
  }

  /**
   * Load a sound with Howler
   */
  private loadSound(key: string, url: string): Promise<void> {
    return new Promise((resolve, reject) => {
      const sound = new Howl({
        src: [url],
        onload: () => {
          this.sounds.set(key, sound);
          resolve();
        },
        onloaderror: (_id, error) => {
          reject(error);
        },
      });
    });
  }

  /**
   * Get a loaded texture
   */
  getTexture(key: string): Texture | undefined {
    return this.textures.get(key);
  }

  /**
   * Get a loaded sound
   */
  getSound(key: string): Howl | undefined {
    return this.sounds.get(key);
  }

  /**
   * Check if a texture exists
   */
  hasTexture(key: string): boolean {
    return this.textures.has(key);
  }

  /**
   * Generate a texture from a Graphics object and cache it
   * @param graphics - The Graphics object to render
   * @param key - The cache key for the texture
   * @param resolution - Optional resolution multiplier
   */
  generateTexture(graphics: Graphics | Container, key: string, resolution?: number): Texture {
    if (!this.renderer) {
      throw new Error('Renderer not set. Call setRenderer() first.');
    }

    const texture = this.renderer.generateTexture({
      target: graphics,
      resolution: resolution ?? 1,
    });

    this.textures.set(key, texture);
    return texture;
  }

  /**
   * Add an existing texture to the cache
   */
  addTexture(key: string, texture: Texture): void {
    this.textures.set(key, texture);
  }

  /**
   * Remove a texture from cache
   */
  removeTexture(key: string): void {
    const texture = this.textures.get(key);
    if (texture) {
      texture.destroy();
      this.textures.delete(key);
    }
  }

  /**
   * Remove a sound from cache
   */
  removeSound(key: string): void {
    const sound = this.sounds.get(key);
    if (sound) {
      sound.unload();
      this.sounds.delete(key);
    }
  }

  /**
   * Create a simple colored rectangle texture
   */
  createRectTexture(
    key: string,
    width: number,
    height: number,
    color: number,
    alpha: number = 1
  ): Texture {
    if (!this.renderer) {
      throw new Error('Renderer not set. Call setRenderer() first.');
    }

    const graphics = new Graphics();
    graphics.rect(0, 0, width, height);
    graphics.fill({ color, alpha });

    const texture = this.generateTexture(graphics, key);
    graphics.destroy();

    return texture;
  }

  /**
   * Create a circle texture
   */
  createCircleTexture(key: string, radius: number, color: number, alpha: number = 1): Texture {
    if (!this.renderer) {
      throw new Error('Renderer not set. Call setRenderer() first.');
    }

    const graphics = new Graphics();
    graphics.circle(radius, radius, radius);
    graphics.fill({ color, alpha });

    const texture = this.generateTexture(graphics, key);
    graphics.destroy();

    return texture;
  }

  /**
   * Destroy all loaded assets
   */
  destroy(): void {
    for (const texture of this.textures.values()) {
      texture.destroy();
    }
    this.textures.clear();

    for (const sound of this.sounds.values()) {
      sound.unload();
    }
    this.sounds.clear();

    this.loadQueue = [];
  }
}

// Global asset loader instance
export const assetLoader = new AssetLoader();
